// Per-sector drift-rules editor for ORACLE settings. TKNE project drift on
// the compass accrues 1 segment per CYCLE a stage is overdue; cycle length is
// per-sector and user-configurable here. Stored as
// user_settings.olmps_drift_rules (JSONB) — Record<Sector, { cycleDays }>,
// partial (missing sector → cycleDays 1). Sector hues come from
// v10/theme SECTOR_COLOR (display-only dots).
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { makeThemedStyles, SECTOR_COLOR } from '../../v10/theme';
import { Field } from '../../v10/ui';
import { SECTORS, SECTOR_LABELS, type Sector } from '../../domain/olmps';
import { useSettingColumn } from './useSettingColumn';

export type DriftRule = { cycleDays: number };
export type DriftRules = Partial<Record<Sector, DriftRule>>;

const DEFAULT_CYCLE_DAYS = 1;

export default function DriftRulesEditor({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const { value: rules, setValue: setRules, loading } = useSettingColumn<DriftRules>(userId, 'olmps_drift_rules', {});

  const cycleDaysFor = (sector: Sector) => rules[sector]?.cycleDays ?? DEFAULT_CYCLE_DAYS;

  const commitCycleDays = (sector: Sector, raw: string) => {
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n) || n <= 0) return;
    setRules({ ...rules, [sector]: { cycleDays: n } });
  };

  const applyToAll = (n: number) => {
    const next: DriftRules = {};
    for (const sector of SECTORS) next[sector] = { cycleDays: n };
    setRules(next);
  };

  if (loading) {
    return (
      <View style={styles.content}>
        <Text style={styles.hint}>Loading…</Text>
      </View>
    );
  }

  return (
    <View style={styles.content}>
      <Text style={styles.hint}>
        TKNE project drift accrues 1 segment per cycle a stage is overdue. Set how many days make up a cycle,
        per sector.
      </Text>

      <View style={styles.row}>
        <Text style={styles.label}>Set all sectors</Text>
        <Field
          inputStyle={styles.numInput}
          keyboardType="number-pad"
          defaultValue={String(DEFAULT_CYCLE_DAYS)}
          onEndEditing={(e) => {
            const n = parseInt(e.nativeEvent.text, 10);
            if (Number.isFinite(n) && n > 0) applyToAll(n);
          }}
        />
      </View>

      {SECTORS.map((sector) => (
        <View key={sector} style={styles.row}>
          <View style={styles.sectorLabel}>
            <View style={[styles.dot, { backgroundColor: SECTOR_COLOR[sector] }]} />
            <Text style={styles.label}>{SECTOR_LABELS[sector]}</Text>
          </View>
          <View style={styles.cycleField}>
            <Field
              inputStyle={styles.numInput}
              keyboardType="number-pad"
              defaultValue={String(cycleDaysFor(sector))}
              onEndEditing={(e) => commitCycleDays(sector, e.nativeEvent.text)}
            />
            <Text style={styles.suffix}>days</Text>
          </View>
        </View>
      ))}
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, radii }) =>
  StyleSheet.create({
  content: { paddingHorizontal: 16, paddingBottom: 48, gap: 8 },
  hint: { fontSize: 12, color: t.ivorySoft, marginTop: 6, fontStyle: 'italic' },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginTop: 8,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  sectorLabel: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  dot: { width: 10, height: 10, borderRadius: 5 },
  label: { fontSize: 14, fontWeight: '700', color: t.ivory },
  cycleField: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  numInput: {
    minWidth: 48,
    textAlign: 'right',
    fontWeight: '700',
  },
  suffix: { fontSize: 12, color: t.ivorySoft, fontWeight: '600' },
}),
);
